import { useEffect, useMemo, useRef, useState } from 'react';
import { Mail, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { outreachAction } from '@/lib/outreach';
import {
  DEFAULT_OUTREACH_SETTINGS,
  DEFAULT_OUTREACH_STEPS,
  type OutreachParameter,
  type OutreachSettings,
  type OutreachStep,
  outreachValues,
} from '@shared/outreach';
import { WEEKDAY_LABELS } from './outreach-labels';
import { OutreachParameters } from './OutreachParameters';
import { OutreachPreview } from './OutreachPreview';

export interface SetupProspect {
  recordId: string;
  title: string;
  cells: Array<{ key: string; value: unknown }>;
  person: { fullName: string; jobTitle: string | null; email: string | null };
}

interface OutreachSetupProps {
  toolAppId: string;
  /** The campaign whose steps and settings these are. */
  sequenceId: string;
  /** Only the campaign's sender changes it. */
  isOwner: boolean;
  view: 'steps' | 'settings';
  steps: OutreachStep[] | null;
  settings: OutreachSettings | null;
  parameters: OutreachParameter[];
  prospects: SetupProspect[];
  senderName: string;
  canManage: boolean;
  onSaved: () => void;
  onParametersChanged: () => void;
}

const field = 'w-full rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-2 text-sm text-domino-ink focus:border-domino-aubergine focus:outline-none';
const label = 'text-xs font-medium text-domino-inkSoft';
type Target = { step: number; part: 'subject' | 'body' };

/**
 * The sequence of a campaign, as lemlist draws it: e-mails one under the other,
 * the wait between them, each a template with parameters, beside the e-mails
 * assembled for one prospect; then when and how much the mailbox sends.
 */
export const OutreachSetup = (props: OutreachSetupProps) => {
  const { sequenceId, view, steps, settings, onSaved } = props;
  const [draftSteps, setDraftSteps] = useState<OutreachStep[]>(steps ?? DEFAULT_OUTREACH_STEPS);
  const [draft, setDraft] = useState<OutreachSettings>(settings ?? DEFAULT_OUTREACH_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const last = useRef<{ target: Target; start: number; end: number }>({ target: { step: 0, part: 'body' }, start: 0, end: 0 });

  useEffect(() => { if (steps) setDraftSteps(steps); }, [steps]);
  useEffect(() => { if (settings) setDraft(settings); }, [settings]);

  const previewProspects = useMemo(() => props.prospects.map((prospect) => ({
    recordId: prospect.recordId, title: prospect.title, person: prospect.person.fullName,
    values: outreachValues({ cells: prospect.cells, person: prospect.person, senderName: props.senderName, signature: draft.signature }),
  })), [props.prospects, props.senderName, draft.signature]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await outreachAction({ action: 'save_sequence', sequenceId, steps: draftSteps, settings: draft });
      setMessage('Enregistré');
      onSaved();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : 'Non enregistré.');
    } finally {
      setSaving(false);
    }
  };
  const setStep = (index: number, patch: Partial<OutreachStep>) =>
    setDraftSteps((current) => current.map((step, at) => at === index ? { ...step, ...patch } : step));
  const remember = (target: Target) => (event: { currentTarget: HTMLInputElement | HTMLTextAreaElement }) => {
    last.current = { target, start: event.currentTarget.selectionStart ?? 0, end: event.currentTarget.selectionEnd ?? 0 };
  };
  /** Puts {{key}} where the cursor last was. */
  const insert = (key: string) => {
    const { target, start, end } = last.current;
    const text = draftSteps[target.step]?.[target.part] ?? '';
    const token = `{{${key}}}`;
    setStep(target.step, { [target.part]: `${text.slice(0, start)}${token}${text.slice(end)}` });
    last.current = { target, start: start + token.length, end: start + token.length };
  };

  return (
    <div className="space-y-4">
      {view === 'steps' && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="space-y-3">
            <OutreachParameters toolAppId={props.toolAppId} parameters={props.parameters} canManage={props.canManage} onInsert={insert} onChanged={props.onParametersChanged} />
            <ol>
              {draftSteps.map((step, index) => (
                <li key={index}>
                  {index > 0 && (
                    <div className="flex items-center gap-2 py-2 pl-5 text-xs text-domino-inkSoft">
                      <span className="h-6 border-l border-dashed border-domino-cardBorder" />
                      Attendre
                      <input type="number" min={1} value={step.delayDays} aria-label={`Jours avant la relance ${index}`}
                        onChange={(event) => setStep(index, { delayDays: Math.max(1, Number(event.target.value) || 1) })}
                        className="w-14 rounded-md border border-domino-cardBorder bg-domino-white px-2 py-1 text-xs text-domino-ink" />
                      jours ouvrés
                    </div>
                  )}
                  <div className="space-y-2 rounded-xl border border-domino-cardBorder bg-domino-white p-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="flex items-center gap-2 text-sm font-semibold text-domino-ink">
                        <Mail className="h-4 w-4 text-domino-aubergine" aria-hidden />
                        {index === 0 ? 'Premier e-mail' : `Relance ${index}`}
                      </p>
                      {index > 0 && (
                        <button type="button" aria-label={`Retirer la relance ${index}`} onClick={() => setDraftSteps((current) => current.filter((_, at) => at !== index))}
                          className="rounded-md p-1 text-domino-inkSoft hover:bg-domino-plumTint hover:text-domino-aubergine">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {index === 0 && (
                      <input value={step.subject} aria-label="Objet" placeholder="Objet" onChange={(event) => setStep(index, { subject: event.target.value })}
                        onSelect={remember({ step: index, part: 'subject' })} onFocus={remember({ step: index, part: 'subject' })} className={field} />
                    )}
                    <textarea rows={index === 0 ? 8 : 5} value={step.body} aria-label={index === 0 ? 'Texte du premier e-mail' : `Texte de la relance ${index}`}
                      onChange={(event) => setStep(index, { body: event.target.value })}
                      onSelect={remember({ step: index, part: 'body' })} onFocus={remember({ step: index, part: 'body' })} className={field} />
                  </div>
                </li>
              ))}
              <li className="pt-3">
                <Button variant="ghost" size="sm" onClick={() => setDraftSteps((current) => [...current, { delayDays: 3, subject: '', body: 'Bonjour {{prenom|}},\n\n\n\n{{signature}}' }])}>
                  <Plus className="mr-1 h-4 w-4" /> Ajouter une relance
                </Button>
              </li>
            </ol>
          </div>
          <div className="lg:sticky lg:top-4 lg:self-start">
            <OutreachPreview steps={draftSteps} prospects={previewProspects} />
          </div>
        </div>
      )}

      {view === 'settings' && (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1 sm:col-span-2">
            <span className={label}>Qui contacter</span>
            <input value={draft.target} onChange={(event) => setDraft({ ...draft, target: event.target.value })} className={field} />
          </label>
          <label className="space-y-1 sm:col-span-2">
            <span className={label}>Signature</span>
            <textarea rows={2} value={draft.signature} onChange={(event) => setDraft({ ...draft, signature: event.target.value })} className={field} />
          </label>
          <label className="space-y-1">
            <span className={label}>E-mails par jour</span>
            <input type="number" min={1} value={draft.dailyLimit} onChange={(event) => setDraft({ ...draft, dailyLimit: Math.max(1, Number(event.target.value) || 1) })} className={field} />
          </label>
          <div className="space-y-1">
            <span className={label}>Plage d’envoi</span>
            <div className="flex items-center gap-2">
              <input type="time" value={draft.windowStart} aria-label="Début" onChange={(event) => setDraft({ ...draft, windowStart: event.target.value })} className={field} />
              <span className="text-domino-inkSoft">–</span>
              <input type="time" value={draft.windowEnd} aria-label="Fin" onChange={(event) => setDraft({ ...draft, windowEnd: event.target.value })} className={field} />
            </div>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <span className={label}>Jours</span>
            <div className="flex flex-wrap gap-1.5">
              {WEEKDAY_LABELS.map((name, index) => {
                const day = index + 1;
                const on = draft.days.includes(day);
                return (
                  <button key={name} type="button" aria-pressed={on}
                    onClick={() => setDraft({ ...draft, days: on ? draft.days.filter((item) => item !== day) : [...draft.days, day].sort() })}
                    className={on ? 'rounded-lg bg-domino-aubergine px-3 py-1.5 text-xs font-semibold text-white' : 'rounded-lg border border-domino-cardBorder px-3 py-1.5 text-xs text-domino-inkSoft'}>
                    {name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {props.isOwner && <div className="flex items-center gap-3">
        <Button size="sm" loading={saving} disabled={saving || draftSteps.some((step) => !step.body.trim()) || draft.days.length === 0 || draft.windowStart >= draft.windowEnd}
          onClick={() => { void save(); }}>
          Enregistrer
        </Button>
        {message && <span role="status" className="text-xs text-domino-inkSoft">{message}</span>}
      </div>}
    </div>
  );
};
